<template>
  <v-app id="ism-app" :class="{ 'dark-mode': isDark }">
    <!-- ══════════ SIDEBAR ══════════ -->
    <aside class="ism-sidebar" :class="{ 'is-collapsed': sidebarCollapsed }">
      <router-link to="/superadmin/dashboard" class="ism-sb-logo">
        <div class="ism-logo-container">
          <img
            :src="isDark ? '/favicon_white.ico' : '/favicon.svg'"
            alt="L"
            class="ism-logo-img ism-logo-img--small"
          />
          <img
            :src="isDark ? '/WhiteLogo.svg' : '/LogoSvg.svg'"
            alt="iSmartSociety"
            class="ism-logo-img ism-logo-img--large"
          />
        </div>
      </router-link>

      <button
        class="ism-sb-toggle"
        @click="sidebarCollapsed = !sidebarCollapsed"
        :title="sidebarCollapsed ? 'Expand' : 'Collapse'"
      >
        <v-icon size="14" class="ism-toggle-icon">{{
          sidebarCollapsed ? "mdi-chevron-right" : "mdi-chevron-left"
        }}</v-icon>
      </button>

      <nav class="ism-sb-nav">
        <template v-for="item in navigationItems">
          <!-- Single item -->
          <v-tooltip
            v-if="!item.childrens"
            right
            content-class="tooltip-right"
            :key="item.title"
            nudge-right="10"
            open-delay="400"
          >
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <router-link
                  :to="item.route"
                  class="ism-sb-item"
                  exact-active-class="ism-sb-item--active"
                >
                  <span class="ism-sb-item-icon"
                    ><v-icon size="20">{{ item.icon }}</v-icon></span
                  >
                  <span class="ism-sb-item-label">{{ item.title }}</span>
                </router-link>
              </div>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>

          <!-- Group item -->
          <div v-else :key="'g_' + item.title" class="ism-sb-group">
            <v-tooltip
              right
              content-class="tooltip-right"
              nudge-right="10"
              open-delay="400"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="ism-sb-item ism-sb-group-header"
                  :class="{ 'ism-sb-item--active': item.active }"
                  @click="toggleGroup(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span class="ism-sb-item-icon"
                    ><v-icon size="20">{{ item.icon }}</v-icon></span
                  >
                  <span class="ism-sb-item-label">{{ item.title }}</span>
                  <v-icon
                    size="14"
                    class="ism-sb-chevron"
                    :class="{ 'is-open': item.active }"
                    >mdi-chevron-down</v-icon
                  >
                </div>
              </template>
              <span>{{ item.title }}</span>
            </v-tooltip>

            <transition name="ism-sub-slide">
              <div v-if="item.active" class="ism-sb-subnav">
                <v-tooltip
                  v-for="sub in item.childrens"
                  :key="sub.title"
                  right
                  content-class="tooltip-right"
                  nudge-right="10"
                  open-delay="400"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <router-link
                        :to="sub.route"
                        class="ism-sb-subitem"
                        exact-active-class="ism-sb-subitem--active"
                      >
                        <v-icon size="16" class="ism-sb-sub-icon">{{
                          sub.icon
                        }}</v-icon>
                        <span class="ism-sb-sub-label">{{ sub.title }}</span>
                      </router-link>
                    </div>
                  </template>
                  <span>{{ sub.title }}</span>
                </v-tooltip>
              </div>
            </transition>
          </div>
        </template>
      </nav>
    </aside>

    <!-- ══════════ TOPBAR ══════════ -->
    <header
      class="ism-topbar"
      :style="{ left: sidebarCollapsed ? '76px' : '260px' }"
    >
      <div class="ism-topbar-left">
        <div class="ism-topbar-page-title">
          <span class="ism-welcome-txt">Welcome,</span>
          <span class="ism-user-name">{{ user.name }}</span>
          <span class="ism-role-tag">Super Admin</span>
        </div>
      </div>

      <div class="ism-topbar-right">
        <ThemeToggle />
        <div class="ism-divider-v"></div>
        <v-menu
          transition="slide-y-transition"
          bottom
          offset-y
          left
          min-width="220"
          content-class="ism-user-menu-pop"
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="ism-avatar-btn" v-bind="attrs" v-on="on">
              <PremiumAvatar
                :image="
                  user.profile_url && !avatarError
                    ? API_BASE_URL +
                      '/uploads/super_admins/profile_pics/' +
                      user.profile_url
                    : null
                "
                :name="user.name"
                :size="36"
              />
              <v-icon size="14" color="var(--text-secondary)"
                >mdi-chevron-down</v-icon
              >
            </div>
          </template>
          <div class="ism-user-menu-inner">
            <div class="ism-menu-user-hdr">
              <PremiumAvatar
                :image="
                  user.profile_url && !avatarError
                    ? API_BASE_URL +
                      '/uploads/super_admins/profile_pics/' +
                      user.profile_url
                    : null
                "
                :name="user.name"
                :size="40"
                variant="squircle"
              />
              <div>
                <div class="ism-menu-name">{{ user.name }}</div>
                <div class="ism-menu-mail">{{ user.email }}</div>
              </div>
            </div>
            <div class="ism-menu-divider"></div>
            <button @click="showProfileModal = true" class="ism-menu-item">
              <v-icon size="18">mdi-account-circle-outline</v-icon> My Profile
            </button>
            <button @click="showPasswordModal = true" class="ism-menu-item">
              <v-icon size="18">mdi-key-outline</v-icon> Change Password
            </button>
            <div class="ism-menu-divider"></div>
            <button
              class="ism-menu-item ism-menu-item--danger"
              @click="confirmLogout = true"
            >
              <v-icon size="18">mdi-logout-variant</v-icon> Log Out
            </button>
          </div>
        </v-menu>
      </div>
    </header>

    <main
      class="ism-main-content"
      :class="{ 'sidebar-collapsed': sidebarCollapsed }"
    >
      <div class="ism-topbar-spacer"></div>
      <Alert />
      <ConfirmModal
        :isOpen="confirmLogout"
        type="logout"
        title="Logout"
        message="Are you sure you want to log out?"
        confirmText="Yes, Logout"
        cancelText="Stay"
        @confirm="logout"
        @cancel="confirmLogout = false"
      />
      <div class="ism-page-content">
        <router-view @changeProfilePic="changeProfilePic" />
      </div>
    </main>

    <Profile
      v-if="showProfileModal"
      v-model="showProfileModal"
      @changeProfilePic="changeProfilePic"
    />
    <ChangePassword v-if="showPasswordModal" v-model="showPasswordModal" />
  </v-app>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import Alert from "../../Alert.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import ThemeToggle from "../../common/ThemeToggle.vue";
import PremiumAvatar from "../../common/PremiumAvatar.vue";
import Profile from "../Profile.vue";
import ChangePassword from "../auth/ChangePassword.vue";

export default {
  name: "AdminLayout",
  components: {
    Alert,
    ConfirmModal,
    ThemeToggle,
    Profile,
    ChangePassword,
    PremiumAvatar,
  },
  data: () => ({
    sidebarCollapsed: false,
    confirmLogout: false,
    avatarError: false,
    showProfileModal: false,
    showPasswordModal: false,
    user: { name: "", email: "", profile_url: null },
    navigationItems: [
      {
        title: "Dashboard",
        icon: "mdi-view-dashboard-outline",
        route: "/superadmin/dashboard",
      },
      {
        title: "Society",
        icon: "mdi-office-building-outline",
        active: false,
        childrens: [
          {
            title: "Requests",
            icon: "mdi-clipboard-text-clock-outline",
            route: "/superadmin/society/requests",
          },
          {
            title: "List",
            icon: "mdi-format-list-bulleted",
            route: "/superadmin/society/list",
          },
        ],
      },
      {
        title: "Super Admins",
        icon: "mdi-shield-account-outline",
        route: "/superadmin/manage-admins",
      },
      {
        title: "Feedbacks",
        icon: "mdi-message-star-outline",
        route: "/superadmin/feedback",
      },
      {
        title: "Plans",
        icon: "mdi-tag-multiple-outline",
        route: "/superadmin/plan",
      },
      {
        title: "Payments",
        icon: "mdi-credit-card-outline",
        route: "/superadmin/payment",
      },
      {
        title: "FAQs",
        icon: "mdi-help-circle-outline",
        route: "/superadmin/faqs",
      },
      {
        title: "CMS",
        icon: "mdi-file-document-edit-outline",
        active: false,
        childrens: [
          {
            title: "Privacy Policy",
            icon: "mdi-shield-lock-outline",
            route: "/superadmin/privacy",
          },
          {
            title: "Terms & Condition",
            icon: "mdi-script-text-outline",
            route: "/superadmin/term_condition",
          },
        ],
      },
    ],
  }),
  watch: {
    $route() {
      this.syncActiveGroup();
    },
    isDark(val) {
      if (this.$vuetify) this.$vuetify.theme.dark = val;
    },
  },
  computed: {
    ...mapState("theme", ["isDark"]),
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    toggleGroup(item) {
      this.navigationItems.forEach((n) => {
        if (n.childrens && n !== item) n.active = false;
      });
      item.active = !item.active;
    },
    logout() {
      this.confirmLogout = false;
      this.$axios.post("/superadmin/logout").then((res) => {
        if (res.data.status) {
          localStorage.removeItem("user");
          this.$router.push("/superadmin/login");
        }
      });
    },
    changeProfilePic() {
      const user = JSON.parse(localStorage.getItem("user"));
      this.user.profile_url = user.profile_url;
    },
    syncActiveGroup() {
      const path = this.$route.path;
      this.navigationItems.forEach((item) => {
        if (item.childrens)
          item.active = item.childrens.some((c) => path.startsWith(c.route));
      });
    },
  },
  mounted() {
    const stored = localStorage.getItem("user");
    if (stored) this.user = JSON.parse(stored);
    this.syncActiveGroup();
    if (this.$vuetify) this.$vuetify.theme.dark = this.isDark;

    // Set professional tab title
    document.title = "iSmartSociety | Super Admin";

    // Update Favicon
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.href = "/favicon_white.ico";

    // Sync body class
    if (this.isDark) document.body.classList.add("dark-mode");
    else document.body.classList.remove("dark-mode");

    this.sidebarCollapsed = window.innerWidth < 1200;
    window.addEventListener("resize", () => {
      if (window.innerWidth < 1200) {
        this.sidebarCollapsed = true;
      }
    });
  },
};
</script>

<style>
/* ── Global ── */
:root {
  --sidebar-width: 260px;
  --sidebar-collapsed: 76px;
  --topbar-height: 64px;
}

/* ── Sidebar ── */
.ism-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--sidebar-width);
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-bdr);
  display: flex;
  flex-direction: column;
  z-index: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ism-sidebar.is-collapsed {
  width: var(--sidebar-collapsed);
}

.ism-sb-logo {
  height: var(--topbar-height);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--sidebar-bdr);
  text-decoration: none;
  padding: 0 16px;
}
.ism-logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.ism-logo-img {
  transition: opacity 0.2s, transform 0.2s;
  object-fit: contain;
}
.ism-logo-img--small {
  width: 32px;
  height: 32px;
  position: absolute;
  opacity: 0;
  transform: scale(0.8);
}
.ism-logo-img--large {
  max-width: 140px;
  max-height: 36px;
  opacity: 1;
}

.is-collapsed .ism-logo-img--small {
  opacity: 1;
  transform: scale(1);
}
.is-collapsed .ism-logo-img--large {
  opacity: 0;
  transform: scale(0.8);
}

.ism-sb-toggle {
  position: absolute;
  top: 50px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 501;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}
.ism-sb-toggle:hover {
  background: var(--accent);
  border-color: var(--accent);
}
.ism-sb-toggle:hover .v-icon {
  color: #fff !important;
}

.ism-sb-nav {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  scrollbar-width: none;
}
.ism-sb-nav::-webkit-scrollbar {
  display: none;
}

.ism-sb-item {
  display: flex;
  align-items: center;
  height: 44px;
  margin: 2px 12px;
  padding: 0 12px;
  border-radius: 10px;
  text-decoration: none;
  color: var(--sidebar-tx) !important;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  overflow: hidden;
}
.ism-sb-item:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--accent) !important;
}
.ism-sb-item--active {
  background: var(--sidebar-act-bg) !important;
  color: var(--sidebar-act-tx) !important;
  font-weight: 600;
}

.ism-sb-item-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}
.is-collapsed .ism-sb-item-icon {
  margin-right: 0;
  width: 100%;
}

.ism-sb-item-label {
  flex: 1;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.is-collapsed .ism-sb-item-label,
.is-collapsed .ism-sb-chevron {
  opacity: 0;
  pointer-events: none;
  width: 0;
}

.ism-sb-chevron {
  transition: transform 0.2s;
  opacity: 0.5;
}
.ism-sb-chevron.is-open {
  transform: rotate(180deg);
}

.ism-sb-subnav {
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.02);
}
.ism-sb-subitem {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px 0 48px;
  font-size: 13px;
  color: var(--sidebar-tx);
  text-decoration: none;
  transition: all 0.2s;
}
.is-collapsed .ism-sb-subitem {
  padding: 0;
  justify-content: center;
  height: 40px;
}
.ism-sb-sub-icon {
  margin-right: 12px;
  display: none;
}
.is-collapsed .ism-sb-sub-icon {
  display: flex;
  margin-right: 0;
}
.ism-sb-sub-label {
  white-space: nowrap;
}
.is-collapsed .ism-sb-sub-label {
  display: none;
}

.ism-sb-subitem:hover {
  color: var(--accent);
}
.ism-sb-subitem--active {
  color: var(--accent) !important;
  font-weight: 700;
}

/* ── Topbar ── */
.ism-topbar {
  position: fixed;
  top: 0;
  right: 0;
  height: var(--topbar-height);
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  z-index: 400;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ism-topbar-left {
  display: flex;
  align-items: center;
  flex: 1;
}
.ism-topbar-page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
}
.ism-welcome-txt {
  color: var(--text-secondary);
  font-weight: 400;
}
.ism-user-name {
  font-weight: 700;
  color: var(--text-primary);
}
.ism-role-tag {
  font-size: 10px;
  font-weight: 800;
  background: var(--accent-lt);
  color: var(--accent);
  padding: 3px 10px;
  border-radius: 6px;
  letter-spacing: 0.01em;
  margin-left: 10px;
  border: 1px solid rgba(242, 101, 34, 0.15);
}

.ism-topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ism-divider-v {
  width: 1px;
  height: 24px;
  background: var(--border-color);
}

.ism-avatar-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 12px;
  transition: background 0.2s;
}
.ism-avatar-btn:hover {
  background: rgba(0, 0, 0, 0.03);
}
.ism-avatar-wrap {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}
.ism-avatar-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ── Content ── */
.ism-main-content {
  margin-left: var(--sidebar-width);
  min-height: 100vh;
  background: var(--bg-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}
.ism-main-content.sidebar-collapsed {
  margin-left: var(--sidebar-collapsed);
}
.ism-topbar-spacer {
  height: var(--topbar-height);
}
.ism-page-content {
  padding: 24px;
  position: relative;
  z-index: 1;
}

#ism-app {
  background: var(--bg-primary) !important;
}

/* Modal Overlays Management */
.v-overlay__scrim {
  background-color: #0f172a !important; /* Deep blue-gray */
  opacity: 0.45; /* Allow Vuetify to override during transitions */
}
.dark-mode .v-overlay__scrim {
  background-color: #000 !important;
  opacity: 0.7;
}

/* ── Menu Popover ── */
.ism-user-menu-pop {
  border-radius: 16px !important;
  box-shadow: var(--shadow-lg) !important;
  border: 1px solid var(--border-color) !important;
}
.ism-user-menu-inner {
  background: var(--card-bg);
  overflow: hidden;
}
.ism-menu-user-hdr {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.ism-menu-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--accent-lt);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}
.ism-menu-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}
.ism-menu-mail {
  font-size: 12px;
  color: var(--text-secondary);
}
.ism-menu-divider {
  height: 1px;
  background: var(--border-color);
}
.ism-menu-item {
  width: 100%;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}
.ism-menu-item:hover {
  background: rgba(0, 0, 0, 0.03);
  color: var(--text-primary);
}
.ism-menu-item v-icon {
  color: inherit !important;
}
.ism-menu-item--danger {
  color: #ef4444 !important;
}
.ism-menu-item--danger:hover {
  background: rgba(239, 68, 68, 0.05);
}

/* ── Responsive ── */
@media (max-width: 1200px) {
  .ism-sb-toggle {
    display: none !important;
  }
}
@media (max-width: 600px) {
  .ism-topbar {
    padding: 0 16px;
  }
  .ism-welcome-txt {
    display: none;
  }
  .ism-page-content {
    padding: 16px;
  }
}
</style>
